"use client";

import { useEffect } from "react";
import { isCursorHydrationNoise } from "@/lib/cursor-browser-hydration";
import { reportClientError } from "@/lib/report-client-error";

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    error.message?.includes("Loading chunk") ||
    error.message?.includes("Failed to fetch dynamically imported module")
  );
}

function isBrowserNoise(message?: string): boolean {
  if (!message) return false;
  return (
    message.includes("ResizeObserver loop") ||
    message.includes("Script error") ||
    isCursorHydrationNoise([message]) ||
    (message.includes("SecurityError") &&
      message.includes("removeEventListener"))
  );
}

export function GlobalErrorReporter() {
  useEffect(() => {
    const onboarding =
      window.location.pathname.startsWith("/seller-onboarding/") ||
      window.location.pathname.startsWith("/buyer-onboarding/");
    try {
      if (!onboarding) sessionStorage.removeItem("venturu_chunk_reload");
    } catch {
      /* Storage may be blocked. */
    }

    function onError(event: ErrorEvent) {
      if (isBrowserNoise(event.message)) {
        return;
      }

      if (
        event.message?.includes("Loading chunk") ||
        event.message?.includes("ChunkLoadError")
      ) {
        reportClientError(
          event.error instanceof Error ? event.error : new Error(event.message),
          { feature: onboarding ? "onboarding" : "ui", stage: "chunk_load" },
        );
        handleChunkReload();
        return;
      }

      if (event.error instanceof Error) {
        reportClientError(event.error);
      }
    }

    function onUnhandledRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;

      if (reason instanceof Error) {
        if (isBrowserNoise(reason.message)) {
          return;
        }

        if (isChunkLoadError(reason)) {
          reportClientError(reason, {
            feature: onboarding ? "onboarding" : "ui",
            stage: "chunk_load",
          });
          handleChunkReload();
          return;
        }

        reportClientError(reason);
      } else if (typeof reason === "string") {
        if (isBrowserNoise(reason)) {
          return;
        }
        reportClientError(new Error(reason));
      }
    }

    function handleChunkReload() {
      const key = "venturu_chunk_reload";
      try {
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
        }
      } catch {
        /* Keep the error visible when a reload cannot be deduplicated. */
      }
    }

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
